import React from 'react';
import { MapPin, CaretDown } from '@phosphor-icons/react';
import { Autocomplete, AutocompleteHandle } from '../Autocomplete';
// Direct import (not the barrel) so bundlers don't pull MobileBottomNav/MobileMenuModal, which
// other files only import dynamically for code-splitting, into whatever chunk this static import
// lands in. See components/navigation/index.ts.
import { Logo } from '../navigation/Logo';
import { navIndexMessages } from '../navigation/nav-live-index/nav-index-messages';
import { SearchWhereChip } from '../navigation/nav-live-index/SearchBarParts';
import { searchBarMessages } from '../navigation/nav-live-index/search-bar-messages';
import type { IntlShape } from 'react-intl';

export interface SearchNavbarProps {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  clearSearch: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
  autocompleteRef?: React.RefObject<AutocompleteHandle | null>;
  onAutocompleteOpen?: () => void;
  className?: string;
  /** Opens the location filter modal (remoteness + location search) from the button attached to
   * the search bar's right edge: this is the only place that modal opens from now that the
   * standalone category-button row is gone. */
  onLocationClick?: () => void;
  isLocationActive?: boolean;
  locationHasFilters?: boolean;
  locationHasExcluded?: boolean;
  /** Navbar live-index redesign (TEC navbar redesign Phase 1), flag-gated: shrinks the search
   * bar and location segment to 40px, and drops the location segment's background-fill "set"
   * state in favour of ink text + accent pin + a small accent dot. Omitted (default false) keeps
   * today's 52px bar and background-fill "set" state exactly as before this ticket. */
  liveIndex?: boolean;
  /** Phase 1 review fix F3: the fully-resolved label to show on the location segment when
   * liveIndex is true (the chosen place's name plus " +N", the remoteness label when only
   * remoteness is set, or the plain "Location" word when nothing is applied) computed by
   * DesktopFilterCategories via resolveLocationSegmentLabel. Ignored when liveIndex is false. */
  locationLabel?: string;
  /** Round 10 (user feedback): the label for the search panel's OWN location pill, rendered only
   * inside the open panel's input row (never the closed bar's segment, which keeps
   * `locationLabel` above): the applied place's name(s), or the localized "Anywhere" word when no
   * location filter is set. Never reflects remoteness (the panel's own work-setup pills already
   * cover that). Computed by DesktopFilterCategories via resolveLocationOnlyLabel. */
  panelLocationLabel?: string;
  /** Whether `panelLocationLabel` reflects an actually-applied location (not the "Anywhere"
   * fallback): drives the pill's applied style (accent border/fill) the same way every other
   * applied item in the panel looks. */
  panelLocationIsSet?: boolean;
  /** Optional slot rendered at the end of this flex-1 cluster, after the fused location picker.
   * Putting a trailing control here (rather than as a sibling in the nav row) keeps it left-aligned
   * with the search bar: the search bar is capped (`max-w-[600px]`) in liveIndex mode, so the
   * flex-1 space that would otherwise push a sibling control right falls to the right of this slot
   * instead. The SRP header uses this for the Advanced Filters button. */
  children?: React.ReactNode;
  /** Sage search bar (2026-09-28, liveIndex only): the filters chip, rendered inside the bar. */
  filtersSegment?: React.ReactNode;
  /** Sage search bar: the SRP's current result count and all open jobs, for the forest count
   * button and the narrowing line (see Autocomplete's own `count`/`countTotal`). */
  count?: number | null;
  countTotal?: number | null;
}

/**
 * Search navbar component - contains logo and search bar
 * Used in DesktopFilterCategories for the left side of the search page header
 */
export const SearchNavbar: React.FC<SearchNavbarProps> = ({
  intl,
  formatMessage,
  clearSearch,
  autocompleteRef,
  onAutocompleteOpen,
  className = '',
  onLocationClick,
  isLocationActive = false,
  locationHasFilters = false,
  locationHasExcluded = false,
  liveIndex = false,
  locationLabel: locationLabelProp,
  panelLocationLabel,
  panelLocationIsSet = false,
  children,
  filtersSegment,
  count = null,
  countTotal = null,
}) => {
  // Plain "Location" word: always the aria-label/fallback, and the flag-off display text
  // (unchanged since before the navbar live-index redesign). Flag on, DesktopFilterCategories
  // resolves the actual chosen place/remoteness name (F3) and passes it as locationLabel; this
  // falls back to the same word when nothing is applied.
  const locationWord = formatMessage({
    id: 'search.refinements.location',
    defaultMessage: 'Location',
    description: 'Filter label for location selection in search refinements',
  });
  // Coordinator review round 4, bug 2: this button renders inside Autocomplete's own header once
  // the panel is open (Autocomplete's `locationSegment` prop), instead of as this component's own
  // always-visible sibling.
  const locationChipState = locationHasExcluded ? 'excluded' : isLocationActive || locationHasFilters ? 'set' : 'idle';
  const locationButton = onLocationClick ? (
    liveIndex ? (
      // Sage search bar (2026-09-28): the Where chip inside the rounded bar. Shows the applied
      // place (or remoteness) name, or the panel's own "Anywhere" word when nothing is set.
      <SearchWhereChip
        onClick={onLocationClick}
        label={formatMessage(searchBarMessages.whereLabel)}
        value={
          // DesktopFilterCategories resolves this from whichever location system is live
          // (legacy `location` or TEC-482 `regionTags`); null/undefined means nothing is set.
          locationLabelProp
            ? locationLabelProp
            : formatMessage({
                id: 'search.refinements.locationAnywhere',
                defaultMessage: 'Anywhere',
                description:
                  "Search panel's own location pill label when no location filter is set (remoteness does not count)",
              })
        }
        ariaLabel={locationWord}
        state={locationChipState}
        ariaExpanded={isLocationActive}
      />
    ) : (
      <button
        type="button"
        onClick={onLocationClick}
        className={`flex items-center justify-center gap-1.5 h-[52px] min-w-[52px] shrink-0 px-4 rounded-l-none rounded-r-full text-sm font-semibold border-2 border-l-0 transition-all duration-(--jc-duration-base) cursor-pointer ${
          locationHasExcluded
            ? 'bg-jc-elevated text-jc-ink border-jc-ink'
            : isLocationActive || locationHasFilters
              ? 'bg-jc-accent-muted text-jc-accent border-jc-accent/45'
              : 'bg-jc-surface/90 text-jc-ink border-jc-border/70'
        }`}
        aria-label={locationWord}
        aria-expanded={isLocationActive}
      >
        <MapPin weight="fill" className="w-4 h-4 shrink-0" />
        <span className="hidden xl:inline whitespace-nowrap">{locationWord}</span>
      </button>
    )
  ) : null;
  // Round 10 (user feedback): the closed-bar segment above (tan fill, trailing dot, fused-corner
  // shape) doesn't fit once it's inside the open panel's own input row: a raised white action
  // pill matching Main.dc.html's "Anywhere" button and every other applied item in the panel
  // (accent border/fill once a value is set), not a re-skin of the closed bar's own look. Inset
  // 10px from the card's right edge, 36px tall, vertically centred in the 54px input row (the
  // panel's header itself supplies that centring via `items-center`); never touching the card's
  // own border.
  const panelLocationPill = onLocationClick ? (
    <button
      type="button"
      onClick={onLocationClick}
      className={`flex items-center gap-1.5 h-9 pl-3 pr-2.5 mr-2.5 rounded-full border shadow-sm text-sm font-semibold shrink-0 whitespace-nowrap ${
        panelLocationIsSet
          ? 'border-jc-accent bg-jc-accent-muted text-jc-ink'
          : 'border-jc-border bg-jc-surface text-jc-ink'
      }`}
      aria-label={locationWord}
      aria-expanded={isLocationActive}
    >
      <MapPin weight="fill" className="w-4 h-4 shrink-0 text-jc-accent" />
      <span className="truncate max-w-[140px]">{panelLocationLabel ?? locationWord}</span>
      <CaretDown weight="bold" className="w-3 h-3 shrink-0 text-jc-ink-secondary" />
    </button>
  ) : null;
  return (
    <div className={`flex items-center gap-3 min-w-0 flex-1 ${className}`}>
      {/* Logo */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <Logo clearSearch={clearSearch} variant="horizontal" locale={intl.locale} />
        <Logo clearSearch={clearSearch} variant="compact" locale={intl.locale} />
      </div>
      {/* Searchbar: no max-width, fills whatever space the row gives it (the old category-button
          row that used to eat this space is gone). The location picker is fused onto its right
          edge (Autocomplete's own right corners squared via `attached`, no gap between the two,
          the button's left border dropped) so the pair reads as one continuous search control
          rather than two separate nav items. */}
      <div className={`min-w-0 flex items-center flex-1 ${liveIndex ? 'max-w-[720px]' : 'w-full'}`}>
        <div className="min-w-0 flex-1">
          <Autocomplete
            ref={autocompleteRef}
            onOpen={onAutocompleteOpen}
            sizeVariant="compact40"
            locationSegment={locationButton}
            panelLocationSegment={panelLocationPill}
            filtersSegment={filtersSegment}
            count={count}
            countTotal={countTotal}
            placeholder={
              liveIndex
                ? formatMessage(navIndexMessages.searchPlaceholder)
                : formatMessage({
                    id: 'instantsearch.searchBoxLink.placeholder',
                    description: 'Placeholder for the search box',
                    defaultMessage: 'Search for jobs',
                  })
            }
          />
        </div>
      </div>
      {children}
    </div>
  );
};
